import { isPasswordAcceptable, type TotpSetup } from '@acasa/shared'
import { useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { AuthError, AuthShell, PRIMARY_BUTTON } from '@/features/auth/auth-shell'
import { PasswordRules } from '@/features/auth/password-rules'
import {
  useAddPasskey,
  useConfirmTotp,
  useEnrolPassword,
  useInvite,
} from '@/features/auth/use-auth'
import { TextInput } from '@/features/handovers/fields'
import { ApiRequestError } from '@/lib/api'

/**
 * Redeeming an invitation: a password, then the authenticator app.
 *
 * Neither is skippable — one without the other is a single factor, and the
 * whole point of this pair is that it is two. The invite is consumed only when
 * the code is confirmed, so a closed tab costs a retry rather than a new
 * invitation.
 *
 * A passkey is offered afterwards, once the account exists and the person is
 * signed in. Deliberately last and deliberately skippable: it is the nicer way
 * in, but an authenticator that will not cooperate must never be the thing
 * standing between somebody and a working account.
 */
export function EnrolmentPage() {
  const { token } = useParams<{ token: string }>()
  const invite = useInvite(token)
  const setPassword = useEnrolPassword(token)
  const confirm = useConfirmTotp(token)
  const addPasskey = useAddPasskey()

  const [password, setPasswordValue] = useState('')
  const [repeat, setRepeat] = useState('')
  const [totp, setTotp] = useState<TotpSetup | null>(null)
  const [code, setCode] = useState('')
  const [showSecret, setShowSecret] = useState(false)
  const [done, setDone] = useState(false)

  if (done) return <Navigate to="/checklists" replace />

  if (invite.isLoading) {
    return (
      <AuthShell title="Einladung wird geprüft">
        <p className="text-muted text-sm">Einen Moment …</p>
      </AuthShell>
    )
  }

  if (invite.isError || !invite.data) {
    return (
      <AuthShell
        title="Einladung ungültig"
        description="Der Link ist abgelaufen oder wurde bereits verwendet."
        footer="Ein Administrator kann eine neue Einladung ausstellen."
      >
        <a
          href="/anmelden"
          className="text-muted hover:text-ink text-xs underline underline-offset-4"
        >
          Zur Anmeldung
        </a>
      </AuthShell>
    )
  }

  /* --------------------------------------- step 3: the optional passkey --- */

  if (confirm.isSuccess) {
    return (
      <AuthShell
        title="Fertig — noch ein Angebot"
        description="Das Konto ist eingerichtet. Ein Passkey auf diesem Gerät macht die Anmeldung zu einem Schritt statt dreien."
        footer="Lässt sich jederzeit in den Einstellungen nachholen oder wieder entfernen."
      >
        <button
          type="button"
          className={PRIMARY_BUTTON}
          disabled={addPasskey.isPending}
          onClick={() =>
            addPasskey.mutate(deviceLabel(), {
              onSuccess: () => setDone(true),
              // A refused or cancelled prompt is not a failure here — the
              // account already works. The error is shown and the way on stays
              // open below.
              onError: () => undefined,
            })
          }
        >
          {addPasskey.isPending ? 'warte auf Bestätigung …' : 'Passkey einrichten'}
        </button>

        <p className="text-faint mt-3 text-xs leading-relaxed">
          Das Gerät fragt nach Face ID, Fingerabdruck oder PIN. Passwort und Code bleiben daneben
          gültig.
        </p>

        <AuthError message={errorMessage(addPasskey.error)} />

        <button
          type="button"
          onClick={() => setDone(true)}
          className="text-muted hover:text-ink mt-6 text-xs underline underline-offset-4 transition-colors"
        >
          Überspringen und loslegen
        </button>
      </AuthShell>
    )
  }

  /* ------------------------------------------------------ step 2: TOTP --- */

  if (totp) {
    return (
      <AuthShell
        title="Schritt 2 von 2: Authenticator"
        description="Der zweite Faktor. Bei jeder Anmeldung wird zusätzlich zum Passwort ein Code aus dieser App abgefragt."
        footer="Am besten in einer App, die synchronisiert (1Password, iOS-Passwörter, Google Authenticator) — sonst ist der Zugang an dieses eine Gerät gebunden."
      >
        <ol className="text-muted flex flex-col gap-4 text-sm leading-relaxed">
          <li>
            <span className="text-ink font-medium">1.</span> QR-Code mit einer Authenticator-App
            scannen.
            <img
              src={totp.qrDataUrl}
              alt="QR-Code zur Einrichtung der Authenticator-App"
              className="border-line bg-paper mt-3 w-[200px] border p-2"
              width={200}
              height={200}
            />
            <button
              type="button"
              onClick={() => setShowSecret((value) => !value)}
              className="text-faint hover:text-muted mt-2 block text-xs underline underline-offset-4"
            >
              {showSecret ? 'Code ausblenden' : 'QR lässt sich nicht scannen?'}
            </button>
            {showSecret && (
              <code className="text-ink mt-2 block font-mono text-xs break-all">{totp.secret}</code>
            )}
          </li>
          <li>
            <span className="text-ink font-medium">2.</span> Den angezeigten 6-stelligen Code hier
            eintragen.
          </li>
        </ol>

        <form
          className="mt-6 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            confirm.mutate(code)
          }}
        >
          <TextInput
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6-stelliger Code"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            required
          />
          <button
            type="submit"
            className={PRIMARY_BUTTON}
            disabled={confirm.isPending || code.length !== 6}
          >
            {confirm.isPending ? 'wird geprüft …' : 'Einrichtung abschließen'}
          </button>
          <AuthError message={errorMessage(confirm.error)} />
        </form>
      </AuthShell>
    )
  }

  /* -------------------------------------------------- step 1: password --- */

  const mismatch = repeat.length > 0 && password !== repeat

  return (
    <AuthShell
      // A re-enrolment is somebody who lost their authenticator, not a newcomer:
      // it says what is about to be replaced instead of welcoming them.
      title={
        invite.data.reenrolment
          ? 'Zugang neu einrichten'
          : `Willkommen, ${invite.data.name.split(' ')[0]}`
      }
      description={
        invite.data.reenrolment
          ? `Konto für ${invite.data.email}. Schritt 1 von 2: ein neues Passwort festlegen. Die bisherige Authenticator-App wird in Schritt 2 ersetzt, und alle anderen Geräte werden nach Abschluss abgemeldet.`
          : `Konto für ${invite.data.email}. Schritt 1 von 2: ein Passwort festlegen.`
      }
      footer="Der Link ist einmalig gültig."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setPassword.mutate(password, { onSuccess: (setup) => setTotp(setup) })
        }}
      >
        {/* A hidden username field is what lets a password manager file the
            entry under the right account instead of guessing. */}
        <input type="email" value={invite.data.email} autoComplete="username" readOnly hidden />

        <TextInput
          type="password"
          autoComplete="new-password"
          placeholder="Passwort"
          value={password}
          onChange={(event) => setPasswordValue(event.target.value)}
          required
        />
        <TextInput
          type="password"
          autoComplete="new-password"
          placeholder="Passwort wiederholen"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          required
        />

        {mismatch && (
          <p className="text-danger text-xs">Die beiden Eingaben stimmen nicht überein.</p>
        )}

        <PasswordRules password={password} subject={invite.data} />

        <button
          type="submit"
          className={PRIMARY_BUTTON}
          disabled={
            setPassword.isPending ||
            password !== repeat ||
            !isPasswordAcceptable(password, invite.data)
          }
        >
          {setPassword.isPending ? 'wird gespeichert …' : 'Weiter'}
        </button>

        <AuthError message={errorMessage(setPassword.error)} />
      </form>
    </AuthShell>
  )
}

/**
 * A rough device name so the list in the settings is readable later. Guessed
 * from the user agent because the alternative — asking — is one more field
 * between somebody and a working account.
 */
function deviceLabel(): string {
  const ua = navigator.userAgent
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua)) return 'iPad'
  if (/Android/.test(ua)) return 'Android-Gerät'
  if (/Macintosh/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows-PC'
  return 'Gerät'
}

function errorMessage(error: unknown): string | null {
  if (!error) return null
  if (error instanceof ApiRequestError) return error.message
  if (error instanceof Error) {
    if (error.name === 'NotAllowedError') return 'Abgebrochen oder nichts ausgewählt.'
    if (error.name === 'InvalidStateError') {
      return 'Auf diesem Gerät ist bereits ein Passkey für dieses Konto vorhanden.'
    }
    return error.message
  }
  return 'Unbekannter Fehler'
}
